import * as requestSystem from "./requestResponse";
import { IBasicCommandResponse } from "../scripts/ah.mb.client-notifications";
import { GlobalEventEmitter } from './global-event-emitter';

interface ITypedResponse<T = any> extends Response {
    /**
     * this will override `json` method from `Body` that is extended by `Response`
     * interface Body {
     *     json(): Promise<any>;
     * }
     */
    json<P = T>(): Promise<P>
}

export interface IFetchOptions {
    handleErrors: boolean;
}

export class FetchWrapper {


    static fetch(input: Request | string, init?: RequestInit, options?: IFetchOptions): Promise<Response> {

        init = FetchWrapper.ensureInit(init);

        return fetch(input, init || { credentials: "same-origin" })
            .then(response => {
                if (!options || options.handleErrors)
                    FetchWrapper.handleErrors(response);
                return response;
            });
    }

    private static ensureInit(init?: RequestInit): RequestInit {
        if (init) {
            init.credentials = "same-origin";
        }
        else
            init = {
                method: "post",
                credentials: "same-origin"
            };

        return init;

    }

    static typedFetch<T>(input: Request | string, init?: RequestInit): Promise<ITypedResponse<T>> {

        init = FetchWrapper.ensureInit(init);

        return fetch(input, init || { credentials: "same-origin" })
            .then(response => FetchWrapper.handleErrors(response));
    }

    static async fetchStream<T>(input: Request | string, init?: RequestInit, callback?: (item: T) => void): Promise<any> {

        init = FetchWrapper.ensureInit(init);
        init = FetchWrapper.ensureHeaders(init);

        return await fetch(input, init || { credentials: "same-origin" })
            // This is a special case for streaming responses, e.g. AI assistant
            .then(async response => {
                const reader = response.body.getReader();
                if (!reader) {
                    throw new Error('Failed to read response');
                }
                const decoder = new JsonStreamDecoder();

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (!value) continue;


                    decoder.decodeChunk<T>(value, item => {
                        if (callback)   
                            callback(item);
                    });
                }
                reader.releaseLock();
            });
    }

    static ensureHeaders(init?: RequestInit): RequestInit {
        const isPost = init.method.toLowerCase() === "post";
        if (isPost && !init.headers)
            init.headers = new Headers({
                'Accept': "application/json",
                'Content-Type': "application/json"
            });

        return init;


    }


    static sendCommandAndHandle(input: Request | string, init?: RequestInit,
        showResponseNotification: boolean = true, additionalInfo: { modelId: string } = null): Promise<IBasicCommandResponse> {
        init = FetchWrapper.ensureInit(init);
        init = FetchWrapper.ensureHeaders(init);
        const isPost = init.method.toLowerCase() === "post";

        const result = fetch(input, init || { credentials: "same-origin" })
            .then(response => FetchWrapper.handleErrors(response))
            .then(response => response.json())
            .then((response: IBasicCommandResponse) => {
                if (showResponseNotification)
                    requestSystem.GlobalFetchRequestsHandler.handle(response);
                if (response.success) {

                    if (isPost && init.body) {

                        // Emit info about the model change, handled for example by undo/redo refresh
                        const bodyObj = JSON.parse(init.body as string);

                        // Some commands e.g. paste don't have a modelId
                        // Then it should be passed in additionalInfo
                        let modelId: string = "";

                        if (bodyObj.hasOwnProperty("modelId"))
                            modelId = bodyObj["modelId"];
                        else
                            if (additionalInfo && additionalInfo.modelId)
                                modelId = additionalInfo.modelId;

                        if (modelId !== "")
                            GlobalEventEmitter.$emit('modelCommandEvent', modelId, response);

                    }
                }

                return response;
            });

        return result;

    }

    static handleErrors(response) {
        // Show global dialog


        if (response.ok) {

            if (response.redirected && response.url.indexOf("AccessDenied") > -1) {
                requestSystem.GlobalFetchRequestsHandler.handle(
                    {
                        success: false,
                        response: "Not authorized to complete this operation"
                    });

                // And stop processing
                throw Error(response.statusText);

            }
            else


                return response;

        }
        else {
            if (response.statusText) {
                const responseObj = {
                    success: false,
                    response: response.status + " " + response.statusText
                };
                requestSystem.GlobalFetchRequestsHandler.handle(responseObj);
            }
            else
                if (response.status == 403)
                    requestSystem.GlobalFetchRequestsHandler.handle({ success: false, response: "403 (forbidden)" });
                else
                    if (response.status == 404) {
                        let urlPath = "";
                        if (response.url) {
                            var urlObj = new URL(response.url)
                            urlPath = urlObj.pathname;
                        }
                        requestSystem.GlobalFetchRequestsHandler.handle({ success: false, response: "404 (not found) " + urlPath });
                    }

                    // Todo show running processes link on timeout
                    //else
                    //    if (response.statius == 504) {
                    //        // Gateway timeout
                    //        requestSystem.GlobalFetchRequestsHandler.handle({ success: false, response: "504 (gateway timeout)" });
                    //    }


                    else {
                        // Try and get info from the raw response
                        response.text()
                            .then((data: string) => {
                                // The response might be too long to display, only take substring
                                let trimmed = data.substring(0, 2000);
                                requestSystem.GlobalFetchRequestsHandler.handle({ success: false, response: response.status.toString() + ": " + trimmed });
                            })

                    }

            // Discontinue processing
            throw Error(response.statusText);
        }
    }

}
class JsonStreamDecoder {

     static JTOKEN_START_OBJECT = '{';
    static JTOKEN_END_OBJECT = '}';

    /** item starts and ends at level 0 */
    private level = 0;

    /** when an item is split in two */
    private partialItem = '';

    private decoder = new TextDecoder();

    public decodeChunk<T>(
        value: Uint8Array,
        decodedItemCallback: (item: T) => void
    ): void {


        const chunk = this.decoder.decode(value);
        let itemStart = 0;

        for (let i = 0; i < chunk.length; i++) {
            if (chunk[i] === JsonStreamDecoder.JTOKEN_START_OBJECT) {
                if (this.level === 0) {
                    itemStart = i;
                }
                this.level++;
            }
            if (chunk[i] === JsonStreamDecoder.JTOKEN_END_OBJECT) {
                this.level--;
                if (this.level === 0) {
                    let item = chunk.substring(itemStart, i + 1);
                    if (this.partialItem) {
                        item = this.partialItem + item;
                        this.partialItem = '';
                    }
                    const parsed = JSON.parse(item);
                    decodedItemCallback(parsed);
                }
            }
        }
        if (this.level !== 0) {
            this.partialItem = chunk.substring(itemStart);
        }
    }

}


// WEBPACK FOOTER //
// ./ClientApp/infrastructure/fetch-wrapper.ts